import { useState } from 'react'

import { api, type ChatMessage, type ChatReply, type MatrixResult } from '../lib/api'
import { Citations } from './InsightsPanel'

interface Turn extends ChatMessage {
  reply?: ChatReply
}

interface Props {
  analysisId: string
  result: MatrixResult
  onSelectTerritory: (territoryId: string) => void
  /** Falso para `viewer`: cada mensagem é uma chamada cobrada ao Vertex, e o backend
   * recusa com 403 de qualquer forma. */
  canChat: boolean
}

/** A conversa vive só nesta tela: recarregar a página apaga. Não há gravação no
 * servidor de propósito — ver `insight/chat.py`. */
export function ChatPanel({ analysisId, result, onSelectTerritory, canChat }: Props) {
  const [briefing, setBriefing] = useState('')
  const [turns, setTurns] = useState<Turn[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const labels: Record<string, string> = Object.fromEntries(result.points.map((p) => [p.territory_id, p.label]))

  const send = () => {
    const content = draft.trim()
    if (!content || sending) return
    const next: Turn[] = [...turns, { role: 'user', content }]
    setTurns(next)
    setDraft('')
    setSending(true)
    setError(null)
    api
      .chat(analysisId, briefing, next.map(({ role, content }) => ({ role, content })))
      .then((reply) => setTurns([...next, { role: 'assistant', content: reply.answer, reply }]))
      .catch((exc: Error) => {
        setError(exc.message)
        setTurns(turns)
        setDraft(content)
      })
      .finally(() => setSending(false))
  }

  return (
    <section className="insights-section chat-section" aria-labelledby="chat-title">
      <header className="section-heading insights-heading">
        <div className="section-label"><span>04</span><p>Conversa estratégica</p></div>
        <div>
          <p className="eyebrow">Pensar junto</p>
          <h2 id="chat-title">Cole o briefing e discuta os territórios</h2>
        </div>
        <p className="section-deck">
          A inteligência responde só com os fatos desta análise e aponta de onde tirou cada
          leitura. Cada mensagem é uma chamada de IA; a conversa não é gravada.
        </p>
      </header>

      {!canChat ? (
        <p className="role-note">Conversar com a inteligência é do papel analista neste projeto.</p>
      ) : (
        <div className="chat-workspace">
          <label className="chat-briefing">
            <span>Briefing do cliente</span>
            <textarea
              value={briefing}
              onChange={(e) => setBriefing(e.target.value)}
              rows={5}
              maxLength={20000}
              placeholder="Cole aqui o briefing: desafio, público, verba, restrições da marca…"
            />
          </label>

          <ol className="chat-log" aria-live="polite">
            {turns.map((turn, index) => (
              <li key={index} className={turn.role === 'user' ? 'chat-user' : 'chat-assistant'}>
                <strong>{turn.role === 'user' ? 'Você' : 'Inteligência'}</strong>
                <p>{turn.content}</p>
                {turn.reply && turn.reply.unverified_numbers.length > 0 && (
                  <p className="chat-warning" role="note">
                    Número sem lastro nos fatos: {turn.reply.unverified_numbers.join(', ')}. Confira
                    no mapa antes de usar.
                  </p>
                )}
                {turn.reply && turn.reply.citations.length > 0 && (
                  <Citations citations={turn.reply.citations} labels={labels} onPick={onSelectTerritory} />
                )}
              </li>
            ))}
            {sending && <li className="chat-assistant"><strong>Inteligência</strong><p>Pensando…</p></li>}
          </ol>

          <form className="chat-input" onSubmit={(e) => { e.preventDefault(); send() }}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
              rows={2}
              maxLength={8000}
              aria-label="Sua mensagem"
              placeholder="Ex.: dado esse briefing, qual território você defenderia e por quê?"
            />
            <button type="submit" className="primary-button" disabled={sending || !draft.trim()}>
              Enviar
            </button>
          </form>
          {error && <p className="brief-error" role="alert">{error}</p>}
        </div>
      )}
    </section>
  )
}
